import React from "react";
import {
    Box,
    Dialog,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { ReceiptLongOutlined } from "@mui/icons-material";
import { useModalStyles } from "../../common/useModalStyles";

export type AuditLogData = {
    entityNumber: string;
    entityName: string;
    action: string;
    auditedObject: any;
    actionType: string;
    clientIP: string;
    auditUser: any;
    auditTimestamp: string;
    userName: string;
};

interface AuditLogDisplayPopupProps {
    open: boolean;
    onClose: () => void;
    title?: string;
    auditLogData: AuditLogData[];
}

const modalContentPx = { xs: 1.5, sm: 2.8 } as const;

const AuditLogDisplayPopup: React.FC<AuditLogDisplayPopupProps> = ({
    open,
    onClose,
    title = "Audit Trail Details",
    auditLogData
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const isDark = theme.palette.mode === "dark";
    const {
        tokens,
        paperSx,
        headerSx,
        iconBoxSx,
        closeIconSx,
        bodySx,
        footerSx,
        cancelBtnSx,
    } = useModalStyles();

    const formatAction = (action: string): { mainAction: string; reason: string } => {
        // Remove "DOS_PREFERENCE_UPDATE: " part if it exists
        const cleanAction = action.replace(/^DOS_PREFERENCE_UPDATE:\s*/, '');

        // Split by "Reason:" to separate main action from reason
        const parts = cleanAction.split(/\.\s*Reason:\s*/);

        return {
            mainAction: parts[0] || cleanAction,
            reason: parts[1] || ''
        };
    };

    const tableHeaderBg = isDark ? "#1C2533" : "#F8FAFC";
    const tableHeaderBorder = isDark ? "rgba(255,255,255,0.05)" : "#EEF2F6";
    const tableRowBg = isDark ? "rgba(255,255,255,0.018)" : "#ffffff";
    const tableRowBorder = isDark ? "rgba(255,255,255,0.04)" : "#EEF2F6";
    const tableRowHover = isDark ? "rgba(255,255,255,0.04)" : "#F1F5F9";
    const tableEmptyBg = isDark ? "rgba(255,255,255,0.02)" : "#F8FAFC";

    const headCellSx = {
        fontWeight: 700,
        fontSize: "0.78rem",
        letterSpacing: "0.02em",
        color: tokens.textSecondary,
        py: 1.25,
        px: 1.75,
        borderBottom: `1px solid ${tableHeaderBorder}`,
        backgroundColor: tableHeaderBg,
        wordBreak: "break-word" as const,
    };

    const bodyCellSx = {
        verticalAlign: "top" as const,
        py: 1.25,
        px: 1.75,
        borderBottom: `1px solid ${tableRowBorder}`,
        fontSize: "0.82rem",
        color: tokens.textPrimary,
        wordBreak: "break-word" as const,
        overflowWrap: "break-word" as const,
    };

    return (
        <Dialog
            open={open}
            keepMounted
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            fullScreen={isMobile}
            scroll="paper"
            PaperProps={{ sx: paperSx(isMobile) }}
            onClick={(event) => {
                event.stopPropagation();
            }}
            aria-describedby="audit-log-display-dialog"
        >
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    flex: 1,
                    minHeight: 0,
                    overflow: "hidden",
                }}
            >
                <Box sx={{ ...headerSx, px: modalContentPx }}>
                    <Box sx={iconBoxSx(tokens.accent)}>
                        <ReceiptLongOutlined sx={{ fontSize: "1.1rem", color: tokens.accent }} />
                    </Box>
                    <Box sx={{ flex: 1, pr: 4 }}>
                        <Typography fontWeight={700} sx={{ fontSize: "1.1rem", color: tokens.textPrimary, lineHeight: 1.3 }}>
                            {title}
                        </Typography>
                        <Typography id="audit-log-display-dialog" sx={{ fontSize: "0.8rem", color: tokens.textSecondary, mt: 0.15 }}>
                            View audit entries with action details and timestamps.
                        </Typography>
                    </Box>
                    <IconButton onClick={onClose} size="small" sx={closeIconSx} aria-label="Close">
                        <CloseIcon sx={{ fontSize: "1rem" }} />
                    </IconButton>
                </Box>

                <Box
                    sx={{
                        ...bodySx,
                        p: 0,
                        px: modalContentPx,
                        py: 2.4,
                        minHeight: 0,
                        minWidth: 0,
                        width: "100%",
                        boxSizing: "border-box",
                        overflowY: "auto",
                        overflowX: "hidden",
                        scrollbarGutter: "stable both-edges",
                        "&::-webkit-scrollbar": { width: 6, height: 6 },
                        "&::-webkit-scrollbar-track": { background: "transparent" },
                        "&::-webkit-scrollbar-thumb": {
                            background: isDark ? "rgba(255,255,255,0.12)" : "#CBD5E1",
                            borderRadius: 4,
                        },
                    }}
                >
                    <TableContainer
                        component={Paper}
                        elevation={0}
                        sx={{
                            mx: 0,
                            my: 2, // CS-2070
                            width: "100%",
                            maxWidth: "100%",
                            boxSizing: "border-box",
                            borderRadius: "12px",
                            border: `1px solid ${tokens.border}`,
                            overflowX: "auto",
                            overflowY: "visible",
                            WebkitOverflowScrolling: "touch",
                            backgroundColor: tableRowBg,
                            boxShadow: "none",
                            "&::-webkit-scrollbar": { height: 6, width: 6 },
                            "&::-webkit-scrollbar-track": { background: "transparent" },
                            "&::-webkit-scrollbar-thumb": {
                                background: isDark ? "rgba(255,255,255,0.12)" : "#CBD5E1",
                                borderRadius: 4,
                            },
                        }}
                    >
                        <Table
                            stickyHeader
                            size="small"
                            sx={{
                                width: "100%",
                                tableLayout: "fixed",
                                minWidth: isMobile ? 560 : 0,
                            }}
                            aria-label="audit log table"
                        >
                            <colgroup>
                                <col style={{ width: "8%" }} />
                                <col style={{ width: "20%" }} />
                                <col style={{ width: "52%" }} />
                                <col style={{ width: "20%" }} />
                            </colgroup>
                            <TableHead>
                                <TableRow sx={{ "& th": { backgroundColor: tableHeaderBg } }}>
                                    <TableCell sx={{ ...headCellSx, width: "8%" }}>
                                        Sr. No.
                                    </TableCell>
                                    <TableCell sx={{ ...headCellSx, width: "20%" }}>
                                        User Name
                                    </TableCell>
                                    <TableCell sx={{ ...headCellSx, width: "52%" }}>
                                        Action
                                    </TableCell>
                                    <TableCell sx={{ ...headCellSx, width: "20%" }}>
                                        Timestamp
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {auditLogData.length > 0 ? (
                                    auditLogData.map((row, index) => {
                                        const { mainAction, reason } = formatAction(row.action);

                                        return (
                                            <TableRow
                                                key={index}
                                                hover
                                                sx={{
                                                    backgroundColor: tableRowBg,
                                                    "&:nth-of-type(even)": {
                                                        backgroundColor: isDark ? "rgba(255,255,255,0.025)" : tokens.cardBg,
                                                    },
                                                    "&:hover": {
                                                        backgroundColor: tableRowHover,
                                                    },
                                                }}
                                            >
                                                <TableCell sx={{ ...bodyCellSx, width: "8%" }}>
                                                    <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: tokens.textPrimary }}>
                                                        {index + 1}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell sx={{ ...bodyCellSx, width: "20%" }}>
                                                    <Typography sx={{ fontSize: "0.82rem", fontWeight: 500, color: tokens.textPrimary }}>
                                                        {row.userName}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell sx={{ ...bodyCellSx, width: "52%" }}>
                                                    <Box sx={{ width: "100%", minWidth: 0 }}>
                                                        <Typography component="div" sx={{ fontSize: "0.82rem", fontWeight: 500, color: tokens.textPrimary, lineHeight: 1.45 }}>
                                                            {mainAction}
                                                        </Typography>
                                                        {reason ? (
                                                            <Typography component="div" sx={{ fontSize: "0.82rem", fontWeight: 500, color: tokens.textPrimary, lineHeight: 1.45, mt: 0.5 }}>
                                                                Reason: {reason}
                                                            </Typography>
                                                        ) : null}
                                                    </Box>
                                                </TableCell>
                                                <TableCell sx={{ ...bodyCellSx, width: "20%" }}>
                                                    <Typography sx={{ fontSize: "0.82rem", fontWeight: 500, color: tokens.textPrimary, whiteSpace: "normal", lineHeight: 1.45 }}>
                                                        {row.auditTimestamp}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={4} sx={{ textAlign: "center", py: 4, backgroundColor: tableEmptyBg, borderBottom: "none" }}>
                                            <Typography sx={{ fontSize: "0.82rem", fontWeight: 500, color: tokens.textSecondary }}>
                                                No audit log data available
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>

                <Box sx={{ ...footerSx, px: modalContentPx }}>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            gap: 1.5,
                            flexDirection: { xs: "column", sm: "row" },
                        }}
                    >
                        <Box
                            component="button"
                            type="button"
                            onClick={onClose}
                            sx={{
                                ...cancelBtnSx,
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 0.8,
                                px: 3,
                                py: 1.2,
                                border: "1px solid",
                                cursor: "pointer",
                                width: { xs: "100%", sm: "auto" },
                            }}
                        >
                            Cancel
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Dialog>
    );
};

export default AuditLogDisplayPopup;